import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtilsV2 } from '@shared/utils';
import {
  CardsApi,
  RequestStrategyEnum,
  UsersApi,
  type AddUserCardsRequest,
  type CardResponse,
  type CardsRequest,
  type DeleteUserCardsRequest,
  type WithSignal,
} from '@shared/api';

const getUserCardsEffect = createEffect(({ signal, ...request }: WithSignal<CardsRequest> = {}) => CardsApi.getCards(request, signal));
const addCardsEffect = createEffect(async (request: AddUserCardsRequest) => {
  const user = await UsersApi.addUserCards(request);
  if (!user.success) throw new Error(user.message || 'Error adding cards');
  return { AssignedUserId: request.userIds };
});
const deleteCardEffect = createEffect(async (request: DeleteUserCardsRequest) => {
  const user = await UsersApi.deleteUserCards(request);
  if (!user.success) throw new Error(user.message || 'Error deleting card');
  return { AssignedUserId: request.userIds };
});
const clearEvent = createEvent();

sample({ source: addCardsEffect.doneData, target: getUserCardsEffect, fn: (doneData) => ({ AssignedUserId: doneData.AssignedUserId }) });
sample({ source: deleteCardEffect.doneData, target: getUserCardsEffect, fn: (doneData) => ({ AssignedUserId: doneData.AssignedUserId }) });

const $dataStore = createStore<{ total: number; data: CardResponse[] }>({ total: 0, data: [] })
  .on(getUserCardsEffect.done, (current, data) => (data.result ? { total: data.result.total, data: data.result.data || [] } : current))
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUserCardsEffect.pending, (_, data) => data)
  .on(getUserCardsEffect.fail, (_, data) => ApiUtilsV2.abortApiLoadingFailCaseTrigger(data.error))
  .on(getUserCardsEffect.done, () => false)
  .on(addCardsEffect.pending, (_, data) => data)
  .on(addCardsEffect.fail, () => false)
  .on(deleteCardEffect.pending, (_, data) => data)
  .on(deleteCardEffect.fail, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUserCardsEffect, () => null)
  // .on(getUserCardsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getUserCardsEffect.fail, () => 'Something went wrong while fetching Cards. Please try again.')
  .on(getUserCardsEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(addCardsEffect, () => null)
  // .on(addCardsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addCardsEffect.fail, () => 'Something went wrong while adding Cards. Please try again.')
  .on(addCardsEffect.done, () => null)
  .on(deleteCardEffect, () => null)
  // .on(deleteCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteCardEffect.fail, () => 'Something went wrong while deleting Card. Please try again.')
  .on(deleteCardEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getUserCardsApi = ApiUtilsV2.wrapperApi(getUserCardsEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getUserCardsApi.cancel());

export const $userCardsStore = {
  events: {
    getUserCards: getUserCardsApi.trigger,
    addCards: addCardsEffect,
    deleteCard: deleteCardEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
